import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import DetailRouter from './components/detail';
import WikiBreadcrumbs from './components/WikiBreadcrumbs';
import { useWikiItem } from '../../contexts/WikiItemContext';
import { parseWikiUrl } from '../../utils/wikiUrlParser';
import GlassContainer from '../../components/layout/GlassContainer';
import { BG_COLORS, BORDER_COLORS } from '../../utils/colors';


const WikiItemDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('wiki');
  const { currentItemName } = useWikiItem();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  // Parse RESTful URL structure
  const urlParams = parseWikiUrl(location.pathname);

  const handleBackClick = () => {
    navigate(-1);
  };

  if (!urlParams || !urlParams.itemId) {
    // Invalid URL or missing item ID
    return (
      <GlassContainer>
        <Box sx={{ color: 'white' }}>
          <div>Invalid item URL</div>
        </Box>
      </GlassContainer>
    );
  }

  return (
    <GlassContainer sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', color: 'white' }}>
      {/* Header - DIRECT child of GlassContainer */}
      {currentItemName && (
        <Box sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 2, sm: 3 },
          p: { xs: 1, sm: 1.5, md: 2 },
          background: BG_COLORS.GLASS,
          borderRadius: 2,
          border: `1px solid ${BORDER_COLORS.LIGHT}`,
          gap: { xs: 1, sm: 2 },
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 25px 50px rgba(185, 28, 28, 0.3)',
            borderColor: 'rgba(185, 28, 28, 0.6)',
            background: BG_COLORS.GLASS_LIGHTER,
          },
        }}>
          {isSm ? (
            <Button
              onClick={handleBackClick}
              startIcon={<ArrowBack />}
              variant="outlined"
              size="small"
              sx={{
                color: 'white',
                borderColor: BORDER_COLORS.FOCUS,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: BORDER_COLORS.FOCUS,
                  backgroundColor: BG_COLORS.GLASS,
                },
              }}
            >
              {t('buttons.backToList')}
            </Button>
          ) : (
            <IconButton
              onClick={handleBackClick}
              size="small"
              sx={{
                color: 'white',
                border: `1px solid ${BORDER_COLORS.FOCUS}`,
                borderRadius: 1,
                p: 0.5,
                '&:hover': {
                  borderColor: BORDER_COLORS.FOCUS,
                  backgroundColor: BG_COLORS.GLASS,
                },
              }}
            >
              <ArrowBack sx={{ fontSize: '1rem' }} />
            </IconButton>
          )}
          <WikiBreadcrumbs noBackground={true} />
        </Box>
      )}
      {/* Content - DIRECT child of GlassContainer */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DetailRouter
          key={`${urlParams.source}-${urlParams.itemId}`}
          source={urlParams.source}
          id={urlParams.itemId!}
          typeId={urlParams.typeId}
          subtypeId={urlParams.subtypeId}
          wikiSubCategory={urlParams.subcategoryId}
          raceId={urlParams.raceId}
          skillTypeId={urlParams.skillTypeId}
          classId={urlParams.classId}
          professionId={urlParams.professionId}
        />
      </Box>
    </GlassContainer>
  );
};

export default WikiItemDetailPage;